import { useEffect, useMemo, useState } from 'react';
import { CursorPagination, INITIAL_PAGINATION, type PaginationState, registerNextPageCursor } from '@/components/data-table/cursor-pagination';
import { DataTable } from '@/components/data-table/data-table';
import { QueryErrorBanner } from '@/components/feedback/query-error';
import { ComposeCard } from '@/features/notifications/compose-card';
import { buildMessageColumns } from '@/features/notifications/message-columns';
import { useNotificationMessages } from '@/hooks/queries/notifications/use-notification-messages';
import { useNotificationsMeta } from '@/hooks/queries/notifications/use-notification-meta';
import { getErrorMessage } from '@/lib/api/error';
import type { NotificationMessage } from '@/services/api/notifications';

export const MessagesPanel = () => {
  const meta = useNotificationsMeta();
  const [pagination, setPagination] = useState<PaginationState>(INITIAL_PAGINATION);
  const [editing, setEditing] = useState<NotificationMessage | null>(null);
  const query = useNotificationMessages(pagination.cursors[pagination.currentPage]);

  useEffect(() => {
    const nextCursor = query.data?.cursor;
    if (!nextCursor) return;
    setPagination((previous) => registerNextPageCursor(previous, nextCursor));
  }, [query.data]);

  const columns = useMemo(() => buildMessageColumns({ approvers: meta.data?.approvers ?? [], onAmend: setEditing }), [meta.data]);

  return (
    <div className="flex flex-col gap-4">
      {meta.isError ? <QueryErrorBanner message={`Failed to fetch notification permissions: ${getErrorMessage(meta.error)}`} onRetry={() => meta.refetch()} /> : null}
      {meta.data?.canCompose ? <ComposeCard editing={editing} onDone={() => setEditing(null)} /> : null}
      <div className="flex items-center justify-between">
        <CursorPagination
          ariaLabel="Message pages"
          currentPage={pagination.currentPage}
          onPageChange={(page) => setPagination((previous) => ({ ...previous, currentPage: page }))}
          totalPages={pagination.totalPages}
        />
      </div>
      {query.isError ? (
        <QueryErrorBanner message={`Failed to fetch notification messages: ${getErrorMessage(query.error)}`} onRetry={() => query.refetch()} />
      ) : (
        <DataTable
          columns={columns}
          data={query.data?.messages}
          emptyMessage="No messages yet — compose the first one above"
          getRowId={(row) => row.messageId}
          isLoading={query.isFetching}
        />
      )}
    </div>
  );
};
MessagesPanel.displayName = 'MessagesPanel';
